import type {
  JepxSettlementItem,
  JepxSettlementSummary,
} from '@ebalance/proto/bffuser/v1/jepx_settlement_model_pb'

import { escapeCsvCell } from '~/libs/csv'

const CSV_NEWLINE = '\r\n'

// CSV の項目名は英語名が未定義のため、 表示言語によらず日本語 (conventions.md「CSV の英語表示」)。
const HEADER_ROW = ['清算番号', '件名', '清算額（円）'].join(',')
const ITEM_HEADER_ROW = [
  '内容',
  '数量（MWh）',
  '単価（円/MWh）',
  '金額（円）',
].join(',')

// backend が JEPX の桁のまま渡す数値 (桁区切りと単位は外れている)。
const DECIMAL_VALUE = /^-?\d+(\.\d+)?$/

function groupThousands(integerDigits: string): string {
  return integerDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/**
 * 数値のセル。 数字・符号・小数点・カンマだけで組まれ数式にならないので、 カンマを含む時に囲むだけにする。
 * `escapeCsvCell` を通すと負の数の先頭に `'` が付いて数値でなくなる。
 */
function numericCell(text: string): string {
  return text.includes(',') ? `"${text}"` : text
}

/**
 * JEPX の原文のセル。 `escapeCsvCell` を通す。 `-` 単独は数式として評価されないので、
 * AC-JST-007 の「`-` を出力する」のとおりそのまま出す。
 */
function rawCell(raw: string): string {
  return raw === '-' ? raw : escapeCsvCell(raw)
}

/** 整数部に 3 桁区切りを付ける。 符号と小数部は元のまま。 数値の形でなければ undefined。 */
function groupedDecimal(value: string): string | undefined {
  if (!DECIMAL_VALUE.test(value)) return undefined
  const negative = value.startsWith('-')
  const [integer = '', fraction] = (negative ? value.slice(1) : value).split(
    '.',
  )
  return `${negative ? '-' : ''}${groupThousands(integer)}${fraction === undefined ? '' : `.${fraction}`}`
}

/**
 * 数量・単価のセル (AC-JST-007)。 backend が数値として読めた値は `number`、 読めない値と `-` は
 * `raw` に原文で入り、 JEPX が空で返した時はどちらも無い。 `whenBlank` は空の時のセル。
 */
function figureCell(
  number: { value: string } | undefined,
  raw: string,
  whenBlank: string,
): string {
  if (number !== undefined) {
    const grouped = groupedDecimal(number.value)
    return grouped === undefined ? rawCell(number.value) : numericCell(grouped)
  }
  return raw === '' ? whenBlank : rawCell(raw)
}

/** 清算額・金額のセル: 桁区切りのカンマを付けた整数。 符号は JEPX のまま。 */
function yenCell(amount: bigint): string {
  const negative = amount < 0n
  const digits = (negative ? -amount : amount).toString()
  return numericCell(`${negative ? '-' : ''}${groupThousands(digits)}`)
}

const itemRow = (item: JepxSettlementItem): string =>
  [
    escapeCsvCell(item.label),
    // 数量が無い時は JEPX の値のまま (空は空欄、 `-` は `-`)、 単価が無い時は `-`。
    figureCell(item.quantity, item.quantityRaw, ''),
    figureCell(item.unitPrice, item.unitPriceRaw, '-'),
    yenCell(item.amountYen),
  ].join(',')

/**
 * 清算書の CSV 本文 (AC-JST-007)。 BOM は付けない (`~/libs/downloadCsv` が保存時に付与する)。
 *
 * 数値のセルと金額のセルは `escapeCsvCell` を通さない。 清算番号・件名・内容と、 数値として
 * 読めなかった数量・単価の原文は JEPX の原文なので、 常に通す。
 */
export function buildSettlementCsv(summary: JepxSettlementSummary): string {
  const { settlement } = summary
  if (settlement === undefined) {
    throw new Error('settlement summary has no settlement')
  }

  return [
    HEADER_ROW,
    [
      escapeCsvCell(settlement.settlementNumber),
      escapeCsvCell(settlement.title),
      yenCell(settlement.amountYen),
    ].join(','),
    '',
    ITEM_HEADER_ROW,
    ...summary.items.map(itemRow),
  ].join(CSV_NEWLINE)
}

// ファイル名に使えない文字 (`\ / : * ? " < > |`) と、 改行・タブなどの制御文字。 空白は残す
// (conventions.md「ダウンロードするファイルの名前」)。
const UNUSABLE_IN_FILE_NAME = /[\\/:*?"<>|\p{Cc}]/gu

/**
 * PDF・CSV 共通のファイル名の拡張子を除いた部分: `{市場名}_{件名}` (AC-JST-006・007)。
 * 件名は JEPX の原文から、 使えない文字だけを除く。 清算日は付けない。
 */
export function settlementFileBase(
  marketPrefix: string,
  title: string,
): string {
  return `${marketPrefix}_${title.replace(UNUSABLE_IN_FILE_NAME, '')}`
}
